import React from 'react';
import {
  Box,
  Typography,
  Switch,
  TextField,
  FormControlLabel,
  Paper,
  IconButton,
  Button,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DaySchedule, TimeSlot } from '../types';

type BusinessScheduleData = {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
};

interface BusinessHoursV2Props {
  businessSchedule: BusinessScheduleData;
  onChange: (schedule: BusinessScheduleData) => void;
}

const dayLabels: Record<string, string> = {
  monday: '月曜日',
  tuesday: '火曜日',
  wednesday: '水曜日',
  thursday: '木曜日',
  friday: '金曜日',
  saturday: '土曜日',
  sunday: '日曜日',
};

const BusinessHoursV2: React.FC<BusinessHoursV2Props> = ({ businessSchedule, onChange }) => {
  const handleToggleDay = (day: keyof BusinessScheduleData) => {
    const newSchedule = { ...businessSchedule };
    newSchedule[day] = {
      ...newSchedule[day],
      isOpen: !newSchedule[day].isOpen,
    };

    // 営業日に切り替えた場合、デフォルトの営業時間を追加
    if (newSchedule[day].isOpen && newSchedule[day].timeSlots.length === 0) {
      newSchedule[day].timeSlots = [{ start: '09:00', end: '18:00' }];
    }

    onChange(newSchedule);
  };

  const handleAddTimeSlot = (day: keyof BusinessScheduleData) => {
    const newSchedule = { ...businessSchedule };
    const lastSlot = newSchedule[day].timeSlots[newSchedule[day].timeSlots.length - 1];

    // 前の時間帯の終了時間を参考に、次の開始時間を設定
    const newStart = lastSlot ? lastSlot.end : '09:00';
    const newEnd = lastSlot ? '23:00' : '18:00';

    newSchedule[day].timeSlots.push({ start: newStart, end: newEnd });
    onChange(newSchedule);
  };

  const handleRemoveTimeSlot = (day: keyof BusinessScheduleData, index: number) => {
    const newSchedule = { ...businessSchedule };
    newSchedule[day].timeSlots.splice(index, 1);

    // 営業時間帯が0になった場合は定休日に
    if (newSchedule[day].timeSlots.length === 0) {
      newSchedule[day].isOpen = false;
    }

    onChange(newSchedule);
  };

  const handleUpdateTimeSlot = (
    day: keyof BusinessScheduleData,
    index: number,
    field: 'start' | 'end' | 'label',
    value: string
  ) => {
    const newSchedule = { ...businessSchedule };
    newSchedule[day].timeSlots[index] = {
      ...newSchedule[day].timeSlots[index],
      [field]: value,
    };
    onChange(newSchedule);
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        営業時間
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        各曜日の営業時間を設定してください。＋ボタンで複数の営業時間帯（ランチ・ディナーなど）を追加できます。
      </Typography>

      {Object.entries(dayLabels).map(([day, label]) => {
        const daySchedule = businessSchedule[day as keyof BusinessScheduleData];

        return (
          <Box
            key={day}
            sx={{
              mb: 3,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            {/* 曜日名と営業ON/OFFスイッチ */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={daySchedule.isOpen}
                    onChange={() => handleToggleDay(day as keyof BusinessScheduleData)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontWeight="bold">{label}</Typography>
                    {!daySchedule.isOpen && (
                      <Chip label="定休日" size="small" color="default" />
                    )}
                  </Box>
                }
                sx={{ minWidth: 150 }}
              />

              {daySchedule.isOpen && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddTimeSlot(day as keyof BusinessScheduleData)}
                  variant="outlined"
                >
                  時間帯追加
                </Button>
              )}
            </Box>

            {/* 営業時間帯のリスト */}
            {daySchedule.isOpen && (
              <Box sx={{ pl: 2 }}>
                {daySchedule.timeSlots.map((slot, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <TextField
                      size="small"
                      label="ラベル"
                      placeholder="例: ランチ"
                      value={slot.label || ''}
                      onChange={(e) =>
                        handleUpdateTimeSlot(
                          day as keyof BusinessScheduleData,
                          index,
                          'label',
                          e.target.value
                        )
                      }
                      sx={{ width: 140 }}
                    />

                    <TextField
                      type="time"
                      label="開始"
                      value={slot.start}
                      onChange={(e) =>
                        handleUpdateTimeSlot(
                          day as keyof BusinessScheduleData,
                          index,
                          'start',
                          e.target.value
                        )
                      }
                      size="small"
                      sx={{ width: 130 }}
                      InputLabelProps={{ shrink: true }}
                    />

                    <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>〜</Typography>

                    <TextField
                      type="time"
                      label="終了"
                      value={slot.end}
                      onChange={(e) =>
                        handleUpdateTimeSlot(
                          day as keyof BusinessScheduleData,
                          index,
                          'end',
                          e.target.value
                        )
                      }
                      size="small"
                      sx={{ width: 130 }}
                      InputLabelProps={{ shrink: true }}
                    />

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        handleRemoveTimeSlot(day as keyof BusinessScheduleData, index)
                      }
                      disabled={daySchedule.timeSlots.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Paper>
  );
};

export default BusinessHoursV2;
